/**
 * React Query hooks + types for Retail > Revenue Leakage (/retail/leakage).
 *
 * The Revenue Leakage explorer ranks OMNI merchandise categories (and, on
 * drill-in, the SKUs within a category) by their dollar decline in sales versus
 * the same trailing window last year, so the biggest losses surface first.
 *
 * - useLeakageCategories(months, enabled)  GET /v1/retail/leakage?months=8
 * - useLeakageSkus(months, category)       GET /v1/retail/leakage?months=8&category=<name>
 * - downloadLeakageXlsx(months, category)   GET /v1/retail/leakage/export?months=8[&category=...]
 *
 * Shipped api contract (api #390). Both levels share ONE response envelope and
 * ONE `rows` key; the shape of a row depends on whether a category was passed:
 *
 *   GET /v1/retail/leakage?months=<1..13, default 8>[&category=<name>]
 *     -> {
 *          as_of,      // date string, or null when there is no data
 *          months,     // the trailing window used
 *          scope,      // 'fleet' | 'store'
 *          window,     // { ty: {start,end}, ly: {start,end} } | null
 *          category,   // the drilled category name, or null at the category level
 *          rows,       // category rows (no category param) OR SKU rows (category param)
 *          total,      // sum of `decline` over the shown rows
 *        }
 *
 * At the category level (no `category` param) each row is
 *   { category, ty, ly, decline, comp }.
 * When `category` is passed each row is a SKU
 *   { item_no, descr, subcategory, ty, ly, decline, comp }.
 * `total` in the envelope is the same four-field shape summed across the rows
 * shown ({ ty, ly, decline, comp }), not a bare number.
 *
 * Categories are leadership's product taxonomy, mirrored from CounterPoint's
 * custom item attributes (category = USER_PRODUCT_DEPARTMENT, subcategory =
 * USER_BRAND_TYP). Both are resolved names, so `category` is the label AND the
 * drill token, and there is no separate code or name field. The NULL/blank
 * category is returned under the sentinel `(uncategorized)`, and the Non-sellable
 * category (bag fees / packaging / supplies) is excluded by the backend.
 *
 * Field semantics: `ty` = this-year sales over the trailing `months` window;
 * `ly` = last-year sales over the same window; `decline` = ly - ty in dollars
 * (positive = losing revenue year over year, negative = growth); `comp` =
 * ty/ly - 1 as a decimal ratio (-0.226 = -22.6%, null when ly = 0). The page
 * shows `decline` as a signed YoY change (growth positive, decline negative)
 * and shows "No LY sales" instead of either figure when comp is null, since
 * there is no last-year baseline to compare against. The backend already
 * ranks rows by `decline` descending, biggest dollar loss first.
 *
 * This file is the single reconcile point for any contract drift: adjust the
 * types + the normalizers here and the page follows.
 *
 * RBAC: access is enforced server-side. A caller with retail.omni.read gets the
 * fleet; a store user with stores:store gets an own-store view. The UI adds a
 * matching client gate (retail.omni.read OR stores:store) for a clean
 * access-denied state, and renders whatever the API returns.
 *
 * Resilience: `retry: false`, stable empty defaults, and tolerant normalizers
 * so the page degrades to a "not available yet" state on any 404 / 503 / shape
 * mismatch rather than crashing, matching the OMNI Performance page it mirrors.
 */

import { useQuery } from '@tanstack/react-query';
import { apiGet } from './api';
import { API_BASE } from './apiBase';

export const LEAKAGE_API_BASE = `${API_BASE}/v1/retail/leakage`;

/** Default trailing window, in months, and the selectable options. */
export const DEFAULT_LEAKAGE_MONTHS = 8;
export const LEAKAGE_MONTHS_OPTIONS = [3, 6, 8, 12] as const;

/** Sentinel value the backend uses for the NULL/blank category. */
export const UNCATEGORIZED_CODE = '(uncategorized)';

// ---------------------------------------------------------------------------
// Types (the api #390 contract; reconcile here on any drift)
// ---------------------------------------------------------------------------

/** The four metric fields shared by a category row, a SKU row, and the total. */
export type LeakageMetrics = {
  ty: number | null;
  ly: number | null;
  decline: number | null;
  comp: number | null;
};

/**
 * One merchandise category ranked by its dollar decline versus last year.
 * `category` is leadership's product category name (Beverage, Bulk, Chocohol,
 * Dessert Case, Freezer, Gourmet, International, L&P Collection, Non-Edible,
 * Novelty, Retro), mirrored from CounterPoint and used directly as the drill
 * token. It replaced the raw CounterPoint category code.
 */
export type LeakageCategory = LeakageMetrics & {
  category: string;
};

/** One SKU within a drilled category, same metric shape as a category row. */
export type LeakageSku = LeakageMetrics & {
  item_no: string;
  descr: string | null;
  /** Leadership's subcategory for this SKU; null when untagged. */
  subcategory: string | null;
};

/** The ty/ly date ranges the metrics cover. Each side is a {start,end} pair. */
export type LeakageWindow = {
  ty: { start: string; end: string };
  ly: { start: string; end: string };
} | null;

/** The shared response envelope; `rows` are category rows or SKU rows. */
export type LeakageResponse = {
  as_of?: string | null;
  months: number;
  scope?: 'fleet' | 'store';
  window?: LeakageWindow;
  category?: string | null;
  rows: unknown[];
  /** { ty, ly, decline, comp } summed across the rows shown; raw/untyped here, normalized by normalizeTotal. */
  total?: unknown;
};

// ---------------------------------------------------------------------------
// Normalizers (tolerant reads so a partial/absent payload degrades cleanly)
// ---------------------------------------------------------------------------

function num(v: unknown): number | null {
  return typeof v === 'number' && !Number.isNaN(v) ? v : null;
}

function str(v: unknown): string | null {
  return typeof v === 'string' ? v : null;
}

function isWindow(v: unknown): v is NonNullable<LeakageWindow> {
  if (!v || typeof v !== 'object') return false;
  const o = v as Record<string, unknown>;
  const ty = o.ty as Record<string, unknown> | undefined;
  const ly = o.ly as Record<string, unknown> | undefined;
  return (
    !!ty && typeof ty.start === 'string' && typeof ty.end === 'string' &&
    !!ly && typeof ly.start === 'string' && typeof ly.end === 'string'
  );
}

/**
 * Reads the raw `rows` array into typed category rows, dropping any entry
 * without a category. The backend already ranks by dollar decline descending; a
 * defensive re-sort keeps that order even if it drifts. Returns [] on any shape
 * mismatch.
 */
export function normalizeCategories(raw: unknown): LeakageCategory[] {
  if (!Array.isArray(raw)) return [];
  const rows: LeakageCategory[] = [];
  for (const r of raw) {
    if (!r || typeof r !== 'object') continue;
    const o = r as Record<string, unknown>;
    const category = str(o.category);
    if (!category) continue;
    rows.push({
      category,
      ty: num(o.ty),
      ly: num(o.ly),
      decline: num(o.decline),
      comp: num(o.comp),
    });
  }
  rows.sort((a, b) => (b.decline ?? -Infinity) - (a.decline ?? -Infinity));
  return rows;
}

/**
 * Reads the raw `total` field into a typed LeakageMetrics object. The backend
 * sends `{ ty, ly, decline, comp }`, not a bare number; every field falls back
 * to null on a shape mismatch so the page's null-comp ("No LY sales") check
 * still degrades safely.
 */
export function normalizeTotal(raw: unknown): LeakageMetrics {
  if (!raw || typeof raw !== 'object') return { ty: null, ly: null, decline: null, comp: null };
  const o = raw as Record<string, unknown>;
  return { ty: num(o.ty), ly: num(o.ly), decline: num(o.decline), comp: num(o.comp) };
}

/**
 * Reads the raw `rows` array into typed SKU rows, dropping any entry without an
 * item number, and re-sorts by dollar decline descending. Returns [] on any
 * shape mismatch.
 */
export function normalizeSkus(raw: unknown): LeakageSku[] {
  if (!Array.isArray(raw)) return [];
  const rows: LeakageSku[] = [];
  for (const r of raw) {
    if (!r || typeof r !== 'object') continue;
    const o = r as Record<string, unknown>;
    const item_no = str(o.item_no);
    if (!item_no) continue;
    rows.push({
      item_no,
      descr: str(o.descr),
      subcategory: str(o.subcategory),
      ty: num(o.ty),
      ly: num(o.ly),
      decline: num(o.decline),
      comp: num(o.comp),
    });
  }
  rows.sort((a, b) => (b.decline ?? -Infinity) - (a.decline ?? -Infinity));
  return rows;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Fleet/own-store category leakage for the trailing `months` window. Surfaces
 * `asOf` (null when the backend has no data, which drives the page's empty
 * state), the ty/ly `window`, and `total` (the ty/ly/decline/comp summed
 * across the rows shown).
 */
export function useLeakageCategories(months: number, enabled: boolean) {
  const query = useQuery<LeakageResponse, Error>({
    queryKey: ['leakage', months],
    queryFn: () => apiGet<LeakageResponse>(`${LEAKAGE_API_BASE}?months=${months}`),
    staleTime: 5 * 60_000,
    retry: false,
    enabled,
  });
  const win = isWindow(query.data?.window) ? (query.data?.window as NonNullable<LeakageWindow>) : null;
  return {
    asOf: query.data?.as_of ?? null,
    months: query.data?.months ?? months,
    scope: query.data?.scope ?? null,
    window: win,
    total: normalizeTotal(query.data?.total),
    categories: normalizeCategories(query.data?.rows),
    isLoading: query.isLoading,
    error: query.error,
  };
}

/**
 * SKU-level leakage for a drilled category. The query is disabled (no fetch)
 * until a category is selected. Surfaces `total` (the ty/ly/decline/comp
 * summed across the SKUs shown).
 */
export function useLeakageSkus(months: number, category: string | null) {
  const query = useQuery<LeakageResponse, Error>({
    queryKey: ['leakage-skus', months, category ?? ''],
    queryFn: () =>
      apiGet<LeakageResponse>(
        `${LEAKAGE_API_BASE}?months=${months}&category=${encodeURIComponent(category as string)}`,
      ),
    staleTime: 5 * 60_000,
    retry: false,
    enabled: category !== null,
  });
  return {
    total: normalizeTotal(query.data?.total),
    skus: normalizeSkus(query.data?.rows),
    isLoading: query.isLoading,
    error: query.error,
  };
}

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

/**
 * Triggers a browser download of the Revenue Leakage Excel export.
 * Calls GET /v1/retail/leakage/export?months=N[&category=...] and saves the
 * blob. When `category` is set the export covers that category's SKUs;
 * otherwise it covers the ranked category list. Mirrors downloadOmniXlsx.
 */
export async function downloadLeakageXlsx(months: number, category: string | null): Promise<void> {
  const qs = category
    ? `?months=${months}&category=${encodeURIComponent(category)}`
    : `?months=${months}`;
  const url = `${LEAKAGE_API_BASE}/export${qs}`;
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Export failed: ${res.status} ${text}`);
  }
  const blob = await res.blob();
  const dispo = res.headers.get('content-disposition') ?? '';
  const m = dispo.match(/filename="([^"]+)"/);
  const filename = m?.[1] ?? `revenue-leakage-${months}mo${category ? `-${category}` : ''}.xlsx`;
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}
